import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, Shield, X } from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import { useScrollParallax } from '../../../hooks/useScrollParallax';
import type { LandingPageProps } from '../landingPageProps';
import { PRODUCT_NAME_MARKETING } from '../../../constants';
import { S9_RAO } from '../t04-dala-orbit/t04LandingShared';
import { T05_LEGAL_STAND, T05_LEGAL_TRADEMARK_FOOTNOTE } from '../../products/t05/t05LegalContent';
import { T05SiteFooter } from './T05SiteFooter';
import { T05GlowCard } from './T05GlowCard';
import { T05ProductStack } from './T05ProductStack';
import { T05StarsBackground } from './T05StarsBackground';
import { T05_PRODUCT_STACK_SECTIONS } from './t05ProductStackData';
import { T05_HERO_VIDEO_SRC } from '../../arkorbit-ui/aurora-media';
import {
  T05_CTA_GRADIENT,
  T05_EDGE_GRADIENT,
  T05_STAT_GRADIENTS,
  T05_STEP_GRADIENTS,
  T05_US_CLOUD_GRADIENT,
} from './t05GlowGradients';
import {
  useHeroSpotlight,
  useHeroVideoPlayback,
  useLandingSectionMotion,
  useNavScrollElevation,
  useScrollProgress,
  useStatsCountUp,
} from './useMotionEnhance';
import { useProductStack } from './useProductStack';
import { T05ArkoDropPanel, T05ArkoNavButton, T05ArkoProvider } from './T05ArkoWidget';
import { T05SiteNavBrand } from './T05SiteNavBrand';
import { useT05LeadForm } from '../../../hooks/useT05LeadForm';
import { T05HeroLeadForm } from './T05HeroLeadForm';
import { T05LeadModal } from './T05LeadModal';
import { T05TrustMarquee } from './T05TrustMarquee';
import {
  BETRIEBSART_ARKORBIT_DESC,
  BETRIEBSART_ARKORBIT_TITLE,
  BETRIEBSART_KANZLEI_DESC,
  BETRIEBSART_KANZLEI_TITLE,
  BETRIEBSARTEN_SUB,
  BETRIEBSARTEN_TITLE,
  ORBIS_ALL_IN_ONE,
  ORBIS_ALL_IN_ONE_PLATFORM,
  ORBIS_HERO_LEAD_HOME,
  START_STEP_DEMO_DESC,
  START_STEP_LIVE_DESC,
  START_STEP_LIVE_TITLE,
  WARUM_ORBIS_DEFICIT_DATENSCHUTZ_DESC,
  WARUM_ORBIS_DEFICIT_DATENSCHUTZ_TITLE,
  WARUM_ORBIS_DEFICIT_TOOLCHAOS_DESC,
  WARUM_ORBIS_DEFICIT_TOOLCHAOS_TITLE,
  WARUM_ORBIS_SOLUTION_DESC,
  WARUM_ORBIS_SOLUTION_TITLE,
  WARUM_ORBIS_SUB,
  WARUM_ORBIS_TITLE,
  HOME_BAUSTEINE_TEASER_CTA,
  HOME_BAUSTEINE_TEASER_SUB,
  HOME_BAUSTEINE_TEASER_TITLE,
  VERGLEICH_EDGE_ITEMS,
  VERGLEICH_US_CLOUD_ITEMS,
} from './t05PublicCopy';
import './theme.css';

const WARUM_ORBIS_DEFICITS = [
  {
    title: WARUM_ORBIS_DEFICIT_TOOLCHAOS_TITLE,
    desc: WARUM_ORBIS_DEFICIT_TOOLCHAOS_DESC,
    gradient: T05_STEP_GRADIENTS[0],
  },
  {
    title: WARUM_ORBIS_DEFICIT_DATENSCHUTZ_TITLE,
    desc: WARUM_ORBIS_DEFICIT_DATENSCHUTZ_DESC,
    gradient: T05_STEP_GRADIENTS[1],
  },
];

const START_STEPS = [
  {
    num: '1',
    title: 'Demo',
    desc: START_STEP_DEMO_DESC,
    gradient: T05_STEP_GRADIENTS[0],
  },
  {
    num: '2',
    title: 'Angebot',
    desc: 'Individuelles Angebot nach Kanzleigröße — Setup, Sitze und Module im Gespräch.',
    gradient: T05_STEP_GRADIENTS[1],
  },
  {
    num: '3',
    title: START_STEP_LIVE_TITLE,
    desc: START_STEP_LIVE_DESC,
    gradient: T05_STEP_GRADIENTS[2],
  },
];

const STATS_BAND = [
  { value: ORBIS_ALL_IN_ONE, label: 'Eine Plattform', gradient: T05_STAT_GRADIENTS[0] },
  { value: '1', label: 'Login für alle', gradient: T05_STAT_GRADIENTS[1] },
  { value: '0', label: 'US-Cloud-Zwang', gradient: T05_STAT_GRADIENTS[2] },
  { value: 'Edge', label: 'LEXCORE zentral · Mandant lokal', gradient: T05_STAT_GRADIENTS[3] },
];

const BETRIEBSARTEN = [
  {
    title: BETRIEBSART_ARKORBIT_TITLE,
    desc: BETRIEBSART_ARKORBIT_DESC,
    gradient: T05_STEP_GRADIENTS[0],
  },
  {
    title: BETRIEBSART_KANZLEI_TITLE,
    desc: BETRIEBSART_KANZLEI_DESC,
    gradient: T05_STEP_GRADIENTS[1],
  },
];

const LandingPageT05: React.FC<LandingPageProps> = ({ onLoginClick, onLeadSubmit }) => {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const lead = useT05LeadForm({
    onLeadSubmit,
    defaultInterest: 'Analyse anfragen',
    quelleHero: 't05-hero-inline',
    quelleModal: 't05-modal',
    watchOpenLeadParam: true,
  });

  const productStack = useProductStack();
  const statsRef = useRef<HTMLElement>(null);
  const heroRef = useHeroSpotlight<HTMLElement>();
  const heroVideoRef = useHeroVideoPlayback(0.65);
  const navScrolled = useNavScrollElevation();
  const progressRef = useScrollProgress<HTMLDivElement>();

  useRevealOnScroll();
  useLandingSectionMotion();
  useScrollParallax();
  useStatsCountUp(statsRef);

  const openAudit = () => lead.openDemo();

  return (
    <T05ArkoProvider>
    <div className="landing-template-t05" data-landing-template="t05">
      <div ref={progressRef} className="t05-scroll-progress" aria-hidden>
        <div className="t05-scroll-progress__bar" />
      </div>
      <div className="t05-ambient" data-parallax aria-hidden>
        <div className="t05-ambient__grid" />
        <div className="t05-ambient__orb t05-ambient__orb--hero" data-parallax-bg />
        <div className="t05-ambient__orb t05-ambient__orb--section" data-parallax-bg />
      </div>

      <div className="t05-shell">
        <div className="t05-nav-wrap" data-scrolled={navScrolled || undefined}>
          <nav className="t05-nav" aria-label="Hauptnavigation">
            <T05SiteNavBrand productLabel={PRODUCT_NAME_MARKETING} />
            <div className="t05-nav__links">
              <a href="#vergleich">Warum Self-Hosted</a>
              <a href="#produkte">Lösung</a>
              <Link to="/recht">Rechtliches</Link>
            </div>
            <div className="t05-nav__actions">
              <T05ArkoNavButton />
              <button type="button" className="t05-nav__ghost" onClick={onLoginClick}>
                Login
              </button>
              <button type="button" className="t05-btn t05-btn--primary t05-btn--sm" onClick={openAudit}>
                Demo anfragen
              </button>
            </div>
            <button
              type="button"
              className="t05-nav__toggle"
              onClick={() => setMobileNavOpen((o) => !o)}
              aria-expanded={mobileNavOpen}
              aria-label={mobileNavOpen ? 'Menü schließen' : 'Menü öffnen'}
            >
              {mobileNavOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <div className={`t05-nav__mobile-menu${mobileNavOpen ? ' t05-nav__mobile-menu--open' : ''}`}>
              <a href="#vergleich" onClick={() => setMobileNavOpen(false)}>
                Warum Self-Hosted
              </a>
              <a href="#produkte" onClick={() => setMobileNavOpen(false)}>
                Lösung
              </a>
              <Link to="/recht" onClick={() => setMobileNavOpen(false)}>
                Rechtliches
              </Link>
              <button type="button" onClick={() => { onLoginClick(); setMobileNavOpen(false); }}>
                Login
              </button>
              <button
                type="button"
                onClick={() => {
                  openAudit();
                  setMobileNavOpen(false);
                }}
              >
                Demo anfragen
              </button>
            </div>
          </nav>
          <T05ArkoDropPanel />
        </div>

        <header className="t05-hero t05-hero--compact" data-parallax ref={heroRef}>
          <div className="t05-hero__media" aria-hidden>
            <video
              ref={heroVideoRef}
              className="t05-hero__video"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            >
              <source src={T05_HERO_VIDEO_SRC} type="video/mp4" />
            </video>
            <div className="t05-hero__video-scrim" />
          </div>
          <div className="t05-hero-glow" aria-hidden />
          <h1 className="t05-headline reveal-up">
            <span className="t05-headline__line">Orbis.</span>
            <span className="t05-headline__accent">Die {ORBIS_ALL_IN_ONE_PLATFORM}.</span>
          </h1>
          <p className="t05-lead reveal-up">
            <strong>{PRODUCT_NAME_MARKETING}</strong> — {ORBIS_HERO_LEAD_HOME}
          </p>
          <T05HeroLeadForm lead={lead} />
        </header>

        <div className="t05-below-hero">
          <T05StarsBackground />

        <section
          className="t05-steps t05-container"
          id="warum-orbis"
          data-section-motion
          aria-labelledby="t05-warum-heading"
        >
          <div className="t05-steps__header reveal-up">
            <h2 id="t05-warum-heading" className="t05-title">{WARUM_ORBIS_TITLE}</h2>
            <p className="t05-subtitle">{WARUM_ORBIS_SUB}</p>
          </div>
          <div className="t05-warum stagger-children reveal-up">
            <div className="t05-warum__deficits">
              {WARUM_ORBIS_DEFICITS.map((block) => (
                <T05GlowCard
                  key={block.title}
                  gradient={block.gradient}
                  glowOpacity={0.1}
                  className="t05-warum__deficit"
                  surfaceClassName="t05-glow-card__surface--center"
                >
                  <h3>{block.title}</h3>
                  <p>{block.desc}</p>
                </T05GlowCard>
              ))}
            </div>
            <T05GlowCard
              gradient={T05_EDGE_GRADIENT}
              glowOpacity={0.32}
              className="t05-warum__solution"
              surfaceClassName="t05-glow-card__surface--center"
            >
              <h3>{WARUM_ORBIS_SOLUTION_TITLE}</h3>
              <p>{WARUM_ORBIS_SOLUTION_DESC}</p>
            </T05GlowCard>
          </div>
        </section>

        <div data-section-motion="from-left">
          <T05TrustMarquee />
        </div>

        <section
          className="t05-steps t05-container"
          id="start"
          data-section-motion
          aria-labelledby="t05-steps-heading"
        >
          <div className="t05-steps__header reveal-up">
            <h2 id="t05-steps-heading" className="t05-title">So starten Sie</h2>
          </div>
          <div className="t05-steps__grid stagger-children reveal-up">
            {START_STEPS.map((step) => (
              <T05GlowCard
                key={step.num}
                gradient={step.gradient}
                glowOpacity={0.22}
                surfaceClassName="t05-glow-card__surface--center"
              >
                <span className="t05-steps__num" aria-hidden>{step.num}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </T05GlowCard>
            ))}
          </div>
        </section>

        <section
          className="t05-steps t05-container"
          id="betrieb"
          data-section-motion
          aria-labelledby="t05-betrieb-heading"
        >
          <div className="t05-steps__header reveal-up">
            <h2 id="t05-betrieb-heading" className="t05-title">{BETRIEBSARTEN_TITLE}</h2>
            <p className="t05-subtitle">{BETRIEBSARTEN_SUB}</p>
          </div>
          <div className="t05-steps__grid t05-steps__grid--two stagger-children reveal-up">
            {BETRIEBSARTEN.map((item) => (
              <T05GlowCard
                key={item.title}
                gradient={item.gradient}
                glowOpacity={0.22}
                surfaceClassName="t05-glow-card__surface--center"
              >
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </T05GlowCard>
            ))}
          </div>
        </section>

        <section
          className="t05-steps t05-container"
          id="bausteine-teaser"
          data-section-motion
          aria-labelledby="t05-bausteine-teaser-heading"
        >
          <div className="t05-steps__header reveal-up">
            <h2 id="t05-bausteine-teaser-heading" className="t05-title">
              {HOME_BAUSTEINE_TEASER_TITLE}
            </h2>
            <p className="t05-subtitle">{HOME_BAUSTEINE_TEASER_SUB}</p>
          </div>
          <div className="t05-bausteine-teaser__cta reveal-up">
            <Link to="/produkt/orbis#bausteine" className="t05-btn t05-btn--ghost">
              {HOME_BAUSTEINE_TEASER_CTA} <ArrowRight size={16} aria-hidden />
            </Link>
          </div>
        </section>

        <section className="t05-section t05-container" id="vergleich" data-section-motion="retract">
          <div className="t05-compare">
            <T05GlowCard
              gradient={T05_US_CLOUD_GRADIENT}
              glowOpacity={0.16}
              glowBlur={24}
              className="reveal-left"
            >
              <p className="t05-compare__label">US-Cloud-SaaS</p>
              <h2 className="t05-compare__title">Fremde Server. Fremde Regeln.</h2>
              <ul className="t05-compare__list">
                {VERGLEICH_US_CLOUD_ITEMS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </T05GlowCard>
            <T05GlowCard gradient={T05_EDGE_GRADIENT} glowOpacity={0.3} className="reveal-right">
              <p className="t05-compare__label t05-compare__label--accent">ARKORBIT Edge</p>
              <h2 className="t05-compare__title">Self-Hosted in der EU.</h2>
              <ul className="t05-compare__list t05-compare__list--check">
                {VERGLEICH_EDGE_ITEMS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </T05GlowCard>
          </div>
        </section>

        <T05ProductStack
          sections={T05_PRODUCT_STACK_SECTIONS}
          sectionRef={productStack.sectionRef}
          frameRef={productStack.frameRef}
          activeIndex={productStack.activeIndex}
          navOffset={productStack.navOffset}
          isDesktop={productStack.isDesktop}
          scrollToSection={productStack.scrollToSection}
        />

        <div className="t05-post-pin" data-section-motion="from-right">
          <section
            ref={statsRef}
            className="t05-stats t05-container reveal-up"
            aria-label="Kernzahlen"
          >
            <div className="t05-stats__grid stagger-children">
              {STATS_BAND.map((stat) => (
                <T05GlowCard
                  key={stat.label}
                  gradient={stat.gradient}
                  glowOpacity={0.14}
                  glowBlur={22}
                  haloRadius={18}
                  className="t05-glow-card--stat"
                  surfaceClassName="t05-glow-card__surface--stats"
                >
                  <span
                    className="t05-stats__value"
                    {...(/^\d/.test(stat.value) ? { 'data-countup': stat.value } : {})}
                  >
                    {stat.value}
                  </span>
                  <span className="t05-stats__label">{stat.label}</span>
                </T05GlowCard>
              ))}
            </div>
          </section>

          <section
            className="t05-final-cta t05-container reveal-up"
            aria-labelledby="t05-final-cta-heading"
          >
            <T05GlowCard
              gradient={T05_CTA_GRADIENT}
              glowOpacity={0.22}
              glowBlur={36}
              haloRadius={26}
              surfaceRadius={24}
              className="t05-glow-card--cta"
              surfaceClassName="t05-glow-card__surface--cta"
            >
              <div className="t05-final-cta__inner">
                <h2 id="t05-final-cta-heading" className="t05-title">
                  Orbis in Ihrer Kanzlei sehen
                </h2>
                <p className="t05-subtitle">
                  30 Minuten Demo — Infrastruktur, Datenschutz und Einsatz nach Kanzleigröße.
                </p>
                <div className="t05-cta-actions">
                  <button type="button" className="t05-btn t05-btn--primary" onClick={openAudit}>
                    Demo anfragen
                  </button>
                </div>
              </div>
            </T05GlowCard>
          </section>

          <section
            className="t05-legal t05-legal-teaser t05-section t05-container reveal-up"
            id="rechtliches"
          >
            <div className="t05-legal__inner">
              <header className="t05-legal__header t05-section__header">
                <div className="t05-legal__icon-wrap">
                  <Shield size={22} className="t05-legal__icon" aria-hidden />
                </div>
                <h2 className="t05-title">Rechtliches & Vertrauen</h2>
                <p className="t05-subtitle">
                  {S9_RAO} ist mit Vertragsabschluss verbindlich; ausführlich im AVV. Verträge, Datenschutz
                  und Impressum finden Sie auf einer Übersichtsseite.
                </p>
              </header>
              <div className="t05-legal__cta-wrap">
                <Link to="/recht" className="t05-btn t05-btn--ghost">
                  Alle rechtlichen Dokumente <ArrowRight size={16} aria-hidden />
                </Link>
              </div>
              <p className="t05-legal__note">
                {T05_LEGAL_TRADEMARK_FOOTNOTE} · ARKORBIT AI Austria · UID ATU83006408 · Stand {T05_LEGAL_STAND}
              </p>
            </div>
          </section>
        </div>
        </div>

        <T05SiteFooter variant="landing" />
      </div>

      <T05LeadModal lead={lead} />
    </div>
    </T05ArkoProvider>
  );
};

export default LandingPageT05;
